import { throwErrorWithId } from '@pypestream/utils';

import {
  getCustomerPypes,
  getPypeKeys,
  type ReportingApiCredentialRow,
} from '../../utils';
import { Pype } from '../prostudio.api-service.types';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

export interface ApiCredentialsData {
  apiCredentials: {
    live: ReportingApiCredentialRow[];
    sandbox: ReportingApiCredentialRow[];
  };
  liveAgentPypes: { live: Pype[]; sandbox: Pype[] };
}

/**
 * Loads every Reporting API credential in the project: fetches the project's
 * pypes, then the API keys for each (per env), flattened into credential rows.
 * Also returns the pypes so the credential modals can offer a connection picker.
 * Shared by the create/update/delete invokes for their post-mutation re-fetch.
 */
export const loadApiCredentialsData = async (
  ctx: ProStudioContext
): Promise<ApiCredentialsData> => {
  const projectId = ctx.project?.id;
  const customerId = ctx.project?.projectProStudioSetting?.customerId;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
  }
  if (!customerId) {
    throwErrorWithId('Customer ID is required');
  }

  const liveAgentPypes = await getCustomerPypes({
    uniqueCustomerId: customerId!,
    projectId: projectId!,
  });

  const [live, sandbox] = await Promise.all([
    getPypeKeys({
      pypes: liveAgentPypes.live,
      projectId: projectId!,
      customerId: customerId!,
      env: 'live',
    }),
    getPypeKeys({
      pypes: liveAgentPypes.sandbox,
      projectId: projectId!,
      customerId: customerId!,
      env: 'sandbox',
    }),
  ]);

  return { apiCredentials: { live, sandbox }, liveAgentPypes };
};

export const loadApiCredentials = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<ApiCredentialsData> => loadApiCredentialsData(ctx);
